import React from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../../shared/utils';
import type { BudgetSummary } from '../../../shared/types/budget';

interface BudgetSummaryCardProps {
  summary: BudgetSummary;
  className?: string;
}

export const BudgetSummaryCard: React.FC<BudgetSummaryCardProps> = ({ 
  summary, 
  className = '' 
}) => {
  const getSpendStatus = () => {
    if (summary.spend_percentage > 100) return 'over';
    if (summary.spend_percentage > 80) return 'warning';
    return 'good';
  };

  const spendStatus = getSpendStatus();

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Budget Overview</h3>
        </div>
        {summary.alerts_count > 0 && (
          <div className="flex items-center text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">{summary.alerts_count} alerts</span>
          </div>
        )}
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Allocated</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(summary.total_allocated)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Spent</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(summary.total_spent)}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Budget Usage</span>
          <span className={`text-sm font-medium ${
            spendStatus === 'over' ? 'text-red-600 dark:text-red-400' :
            spendStatus === 'warning' ? 'text-amber-600 dark:text-amber-400' :
            'text-green-600 dark:text-green-400'
          }`}>
            {summary.spend_percentage.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              spendStatus === 'over' ? 'bg-red-500' :
              spendStatus === 'warning' ? 'bg-amber-500' :
              'bg-green-500'
            }`}
            style={{ width: `${Math.min(summary.spend_percentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Remaining Budget */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Remaining</p>
          <p className={`text-lg font-semibold ${
            summary.remaining_budget < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
          }`}>
            {formatCurrency(summary.remaining_budget)}
          </p>
        </div>
        <div className="flex items-center">
          {summary.performance_score > 70 ? (
            <TrendingUp className="h-5 w-5 text-green-500 mr-1" />
          ) : (
            <TrendingDown className="h-5 w-5 text-red-500 mr-1" />
          )}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Performance: {summary.performance_score.toFixed(0)}%
          </span>
        </div>
      </div>
    </div>
  );
};